import { computed, readonly, ref, toValue } from 'vue'
import type { MaybeRefOrGetter } from 'vue'

export const CHECKOUT_PAYMENT_LIFECYCLE_STATES = [
  'idle',
  'creating_order',
  'initializing_payment',
  'redirecting_to_provider',
  'awaiting_provider_return',
  'verifying_payment',
  'paid',
  'accepted',
  'failed_retryable',
  'failed_final',
  'cancelled_retryable',
  'cancelled_final',
  'pending',
  'expired',
  'unknown_needs_support',
] as const

export type CheckoutPaymentLifecycleStateName = (typeof CHECKOUT_PAYMENT_LIFECYCLE_STATES)[number]

export type CheckoutPaymentLifecycleState = {
  [StateName in CheckoutPaymentLifecycleStateName]: {
    name: StateName
    isTerminal: boolean
    isSuccessful: boolean
    reason?: CheckoutPaymentLifecycleReason
    transactionState?: string
    providerReturnEvent?: ProviderReturnEventType
  }
}[CheckoutPaymentLifecycleStateName]

export type CheckoutPaymentLifecycleReason =
  | 'initial'
  | 'manual_transition'
  | 'order_creation_started'
  | 'payment_initialization_started'
  | 'provider_redirect_started'
  | 'provider_return_detected'
  | 'payment_verification_started'
  | 'verified_transaction_paid'
  | 'verified_transaction_accepted'
  | 'verified_transaction_failed'
  | 'verified_transaction_cancelled'
  | 'verified_transaction_pending'
  | 'verified_transaction_expired'
  | 'verified_non_async_payment_accepted'
  | 'missing_transaction'
  | 'unknown_transaction_state'
  | 'mapping_error'

export type PaymentLifecycleAction =
  | 'retry_payment'
  | 'change_payment'
  | 'cancel_order'
  | 'refresh_status'
  | 'contact_support'
  | 'fallback_redirect'
  | 'view_order_details'

export interface PaymentLifecycleActionMeta {
  action: PaymentLifecycleAction
  enabled: boolean
  primary: boolean
  states: readonly CheckoutPaymentLifecycleStateName[]
  href?: string
  reason?: string
}

export interface PaymentLifecycleActionDefinition {
  action: PaymentLifecycleAction
  primary: boolean
  states: readonly CheckoutPaymentLifecycleStateName[]
  hrefContext?: keyof Pick<PaymentLifecycleActionContext, 'providerRedirectUrl' | 'supportUrl' | 'orderDetailsUrl'>
  requiresHref?: boolean
}

export interface PaymentLifecycleActionContext {
  canRetryPayment?: boolean
  canChangePayment?: boolean
  canCancelOrder?: boolean
  providerRedirectUrl?: string | null
  supportUrl?: string | null
  orderDetailsUrl?: string | null
}

export const PROVIDER_RETURN_EVENT_TYPES = [
  'none',
  'provider_returned_success',
  'provider_returned_failure',
  'provider_returned_cancel',
  'provider_returned_pending',
  'provider_returned_unknown',
] as const

export type ProviderReturnEventType = (typeof PROVIDER_RETURN_EVENT_TYPES)[number]

export interface ProviderReturnHint {
  key: string
  value: string
  category: Exclude<ProviderReturnEventType, 'none'>
}

export interface ProviderReturnEvent {
  type: ProviderReturnEventType
  isPaymentProof: false
  hints: readonly ProviderReturnHint[]
  hasQuery: boolean
}

export type ProviderReturnQueryValue = string | number | boolean | null | undefined
export type ProviderReturnQuery =
  string | Readonly<Record<string, ProviderReturnQueryValue | readonly ProviderReturnQueryValue[]>>

export interface PaymentLifecycleRawStateMachineState {
  name?: unknown
  technicalName?: unknown
  translated?: {
    name?: unknown
    technicalName?: unknown
  } | null
}

export interface PaymentLifecycleRawPaymentMethod {
  asynchronous?: unknown
  afterOrderEnabled?: unknown
  synchronous?: unknown
  technicalName?: unknown
  handlerIdentifier?: unknown
  translated?: {
    technicalName?: unknown
    name?: unknown
  } | null
}

export interface PaymentLifecycleRawTransaction {
  createdAt?: unknown
  updatedAt?: unknown
  state?: unknown
  status?: unknown
  technicalName?: unknown
  transactionState?: unknown
  paymentState?: unknown
  stateMachineState?: PaymentLifecycleRawStateMachineState | null
  paymentMethod?: PaymentLifecycleRawPaymentMethod | null
  paymentMethodId?: unknown
}

export interface PaymentLifecycleRawPayment {
  state?: unknown
  status?: unknown
  technicalName?: unknown
  transactionState?: unknown
  paymentState?: unknown
  stateMachineState?: PaymentLifecycleRawStateMachineState | null
  transaction?: PaymentLifecycleRawTransaction | null
  transactions?: readonly PaymentLifecycleRawTransaction[] | null
  redirectUrl?: unknown
  paymentUrl?: unknown
}

export interface PaymentLifecycleRawOrder {
  id?: unknown
  state?: unknown
  status?: unknown
  stateMachineState?: PaymentLifecycleRawStateMachineState | null
  transaction?: PaymentLifecycleRawTransaction | null
  transactions?: readonly PaymentLifecycleRawTransaction[] | null
}

export type PaymentLifecycleRetryPolicy = 'retryable' | 'final'

export interface MapPaymentLifecycleInput {
  order?: PaymentLifecycleRawOrder | null
  payment?: PaymentLifecycleRawPayment | null
  transaction?: PaymentLifecycleRawTransaction | null
  transactions?: readonly PaymentLifecycleRawTransaction[] | null
  providerReturnEvent?: ProviderReturnEvent | null
  retryPolicy?: PaymentLifecycleRetryPolicy
}

export interface UseCheckoutPaymentLifecycleOptions {
  initialState?: CheckoutPaymentLifecycleStateName
  actions?: MaybeRefOrGetter<PaymentLifecycleActionContext | undefined>
}

const TERMINAL_STATES = new Set<CheckoutPaymentLifecycleStateName>([
  'paid',
  'accepted',
  'failed_final',
  'cancelled_final',
  'expired',
  'unknown_needs_support',
])

const SUCCESS_STATES = new Set<CheckoutPaymentLifecycleStateName>(['paid', 'accepted'])

const PAID_TRANSACTION_STATES = new Set(['paid'])

const ACCEPTED_TRANSACTION_STATES = new Set(['authorized', 'in_progress', 'in-progress'])

const FAILED_TRANSACTION_STATES = new Set(['failed', 'failure', 'error', 'errored', 'declined', 'payment_failed'])

const FAILED_FINAL_TRANSACTION_STATES = new Set(['chargeback', 'refunded', 'refunded_partially'])

const CANCELLED_TRANSACTION_STATES = new Set(['cancelled', 'canceled', 'cancel', 'aborted'])

const CANCELLED_FINAL_TRANSACTION_STATES = new Set(['voided', 'void', 'reversed'])

const PENDING_TRANSACTION_STATES = new Set([
  'open',
  'process',
  'processing',
  'pending',
  'unconfirmed',
  'reminded',
  'paid_partially',
  'partially_paid',
])

const EXPIRED_TRANSACTION_STATES = new Set(['expired', 'timeout', 'timed_out', 'timed-out'])

const NON_ASYNC_ACCEPTED_TRANSACTION_STATES = new Set(['open', 'pending', 'in_progress', 'in-progress'])

const SUCCESS_PROVIDER_HINTS = new Set([
  'paid',
  'success',
  'succeeded',
  'successful',
  'complete',
  'completed',
  'authorized',
])
const FAILURE_PROVIDER_HINTS = new Set(['unpaid', 'failed', 'failure', 'error', 'errored', 'declined'])
const CANCEL_PROVIDER_HINTS = new Set(['cancel', 'cancelled', 'canceled', 'aborted'])
const PENDING_PROVIDER_HINTS = new Set(['pending', 'open', 'processing', 'in_progress', 'in-progress'])

const RETURN_HINT_KEYS = new Set([
  'status',
  'payment_status',
  'paymentStatus',
  'paymentstatus',
  'state',
  'result',
  'outcome',
  'success',
  'cancel',
  'cancelled',
  'canceled',
  'error',
  'failure',
])

export const CHECKOUT_PAYMENT_ACTION_METADATA = {
  retry_payment: {
    action: 'retry_payment',
    primary: true,
    states: ['failed_retryable', 'cancelled_retryable'],
  },
  change_payment: {
    action: 'change_payment',
    primary: false,
    states: ['failed_retryable', 'cancelled_retryable'],
  },
  cancel_order: {
    action: 'cancel_order',
    primary: false,
    states: ['failed_retryable', 'cancelled_retryable', 'expired'],
  },
  refresh_status: {
    action: 'refresh_status',
    primary: true,
    states: ['awaiting_provider_return', 'verifying_payment', 'pending', 'unknown_needs_support'],
  },
  contact_support: {
    action: 'contact_support',
    primary: false,
    states: [
      'failed_retryable',
      'failed_final',
      'cancelled_retryable',
      'cancelled_final',
      'expired',
      'unknown_needs_support',
      'pending',
    ],
    hrefContext: 'supportUrl',
  },
  fallback_redirect: {
    action: 'fallback_redirect',
    primary: true,
    states: ['redirecting_to_provider', 'pending'],
    hrefContext: 'providerRedirectUrl',
    requiresHref: true,
  },
  view_order_details: {
    action: 'view_order_details',
    primary: false,
    states: ['paid', 'accepted', 'failed_final', 'cancelled_final', 'expired', 'unknown_needs_support'],
    hrefContext: 'orderDetailsUrl',
    requiresHref: true,
  },
} satisfies Record<PaymentLifecycleAction, PaymentLifecycleActionDefinition>

export const createPaymentLifecycleState = (
  name: CheckoutPaymentLifecycleStateName,
  details: Omit<CheckoutPaymentLifecycleState, 'name' | 'isTerminal' | 'isSuccessful'> = {},
): CheckoutPaymentLifecycleState => ({
  name,
  isTerminal: TERMINAL_STATES.has(name),
  isSuccessful: SUCCESS_STATES.has(name),
  ...details,
})

export const parseProviderReturnEvent = (query: ProviderReturnQuery | null | undefined): ProviderReturnEvent => {
  const entries = normalizeProviderReturnQuery(query)
  const hints = entries.flatMap(([key, value]) => {
    const category = categorizeProviderReturnHint(key, value)

    return category
      ? [
          {
            key,
            value: normalizeToken(value),
            category,
          },
        ]
      : []
  })

  if (!entries.length) {
    return {
      type: 'none',
      isPaymentProof: false,
      hints,
      hasQuery: false,
    }
  }

  const categories = new Set(hints.map((hint) => hint.category))
  const eventType =
    categories.size === 1 ? (hints[0]?.category ?? 'provider_returned_unknown') : 'provider_returned_unknown'

  return {
    type: eventType,
    isPaymentProof: false,
    hints,
    hasQuery: true,
  }
}

export const mapPaymentLifecycleState = (input: MapPaymentLifecycleInput): CheckoutPaymentLifecycleState => {
  try {
    const transaction = selectPaymentLifecycleTransaction(input)
    const transactionState = normalizeTransactionState(transaction ?? input.payment)

    if (!transaction) {
      return createPaymentLifecycleState('unknown_needs_support', {
        reason: 'missing_transaction',
        providerReturnEvent: input.providerReturnEvent?.type,
      })
    }

    if (!transactionState) {
      return createPaymentLifecycleState('unknown_needs_support', {
        reason: 'unknown_transaction_state',
        providerReturnEvent: input.providerReturnEvent?.type,
      })
    }

    return mapTransactionStateToPaymentLifecycleState(transactionState, {
      paymentMethod: getPaymentLifecyclePaymentMethod(transaction),
      retryPolicy: input.retryPolicy,
      providerReturnEvent: input.providerReturnEvent,
    })
  } catch {
    return createPaymentLifecycleState('unknown_needs_support', {
      reason: 'mapping_error',
      providerReturnEvent: input.providerReturnEvent?.type,
    })
  }
}

export const mapTransactionStateToPaymentLifecycleState = (
  transactionState: string,
  options: {
    paymentMethod?: PaymentLifecycleRawPaymentMethod | null
    retryPolicy?: PaymentLifecycleRetryPolicy
    providerReturnEvent?: ProviderReturnEvent | null
  } = {},
): CheckoutPaymentLifecycleState => {
  const normalizedState = normalizeToken(transactionState)
  const retryPolicy = options.retryPolicy ?? 'retryable'
  const providerReturnEvent = options.providerReturnEvent?.type

  if (
    NON_ASYNC_ACCEPTED_TRANSACTION_STATES.has(normalizedState) &&
    isNonAsyncAcceptedPaymentMethod(options.paymentMethod)
  ) {
    return createPaymentLifecycleState('accepted', {
      reason: 'verified_non_async_payment_accepted',
      transactionState: normalizedState,
      providerReturnEvent,
    })
  }

  if (PAID_TRANSACTION_STATES.has(normalizedState)) {
    return createPaymentLifecycleState('paid', {
      reason: 'verified_transaction_paid',
      transactionState: normalizedState,
      providerReturnEvent,
    })
  }

  if (ACCEPTED_TRANSACTION_STATES.has(normalizedState)) {
    return createPaymentLifecycleState('accepted', {
      reason: 'verified_transaction_accepted',
      transactionState: normalizedState,
      providerReturnEvent,
    })
  }

  if (FAILED_TRANSACTION_STATES.has(normalizedState)) {
    return createPaymentLifecycleState(retryPolicy === 'final' ? 'failed_final' : 'failed_retryable', {
      reason: 'verified_transaction_failed',
      transactionState: normalizedState,
      providerReturnEvent,
    })
  }

  if (FAILED_FINAL_TRANSACTION_STATES.has(normalizedState)) {
    return createPaymentLifecycleState('failed_final', {
      reason: 'verified_transaction_failed',
      transactionState: normalizedState,
      providerReturnEvent,
    })
  }

  if (CANCELLED_TRANSACTION_STATES.has(normalizedState)) {
    return createPaymentLifecycleState(retryPolicy === 'final' ? 'cancelled_final' : 'cancelled_retryable', {
      reason: 'verified_transaction_cancelled',
      transactionState: normalizedState,
      providerReturnEvent,
    })
  }

  if (CANCELLED_FINAL_TRANSACTION_STATES.has(normalizedState)) {
    return createPaymentLifecycleState('cancelled_final', {
      reason: 'verified_transaction_cancelled',
      transactionState: normalizedState,
      providerReturnEvent,
    })
  }

  if (PENDING_TRANSACTION_STATES.has(normalizedState)) {
    return createPaymentLifecycleState('pending', {
      reason: 'verified_transaction_pending',
      transactionState: normalizedState,
      providerReturnEvent,
    })
  }

  if (EXPIRED_TRANSACTION_STATES.has(normalizedState)) {
    return createPaymentLifecycleState('expired', {
      reason: 'verified_transaction_expired',
      transactionState: normalizedState,
      providerReturnEvent,
    })
  }

  return createPaymentLifecycleState('unknown_needs_support', {
    reason: 'unknown_transaction_state',
    transactionState: normalizedState,
    providerReturnEvent,
  })
}

export const getPaymentLifecycleActions = (
  lifecycleState: CheckoutPaymentLifecycleStateName | CheckoutPaymentLifecycleState,
  context: PaymentLifecycleActionContext = {},
): PaymentLifecycleActionMeta[] => {
  const stateName = typeof lifecycleState === 'string' ? lifecycleState : lifecycleState.name

  return Object.values(CHECKOUT_PAYMENT_ACTION_METADATA).flatMap((definition) => {
    if (!(definition.states as readonly CheckoutPaymentLifecycleStateName[]).includes(stateName)) {
      return []
    }

    return [buildPaymentLifecycleAction(definition, context)]
  })
}

export const getEnabledPaymentLifecycleActions = (
  lifecycleState: CheckoutPaymentLifecycleStateName | CheckoutPaymentLifecycleState,
  context: PaymentLifecycleActionContext = {},
): PaymentLifecycleActionMeta[] => {
  return getPaymentLifecycleActions(lifecycleState, context).filter((action) => action.enabled)
}

export const getPaymentLifecycleActionStates = (
  action: PaymentLifecycleAction,
): readonly CheckoutPaymentLifecycleStateName[] => {
  return CHECKOUT_PAYMENT_ACTION_METADATA[action].states
}

export const isCheckoutPaymentLifecycleStateName = (value: unknown): value is CheckoutPaymentLifecycleStateName => {
  return (
    typeof value === 'string' && CHECKOUT_PAYMENT_LIFECYCLE_STATES.includes(value as CheckoutPaymentLifecycleStateName)
  )
}

export const useCheckoutPaymentLifecycle = (options: UseCheckoutPaymentLifecycleOptions = {}) => {
  const lifecycleState = ref<CheckoutPaymentLifecycleState>(
    createPaymentLifecycleState(options.initialState ?? 'idle', { reason: 'initial' }),
  )
  const providerReturnEvent = ref<ProviderReturnEvent>(parseProviderReturnEvent(undefined))

  const actions = computed(() => getPaymentLifecycleActions(lifecycleState.value, toValue(options.actions) ?? {}))

  const transitionTo = (
    name: CheckoutPaymentLifecycleStateName,
    details: Omit<CheckoutPaymentLifecycleState, 'name' | 'isTerminal' | 'isSuccessful'> = {},
  ) => {
    lifecycleState.value = createPaymentLifecycleState(name, {
      reason: details.reason ?? 'manual_transition',
      ...details,
    })

    return lifecycleState.value
  }

  const parseProviderReturn = (query: ProviderReturnQuery | null | undefined) => {
    providerReturnEvent.value = parseProviderReturnEvent(query)

    if (providerReturnEvent.value.type !== 'none') {
      transitionTo('verifying_payment', {
        reason: 'provider_return_detected',
        providerReturnEvent: providerReturnEvent.value.type,
      })
    }

    return providerReturnEvent.value
  }

  const applyVerifiedPaymentState = (input: Omit<MapPaymentLifecycleInput, 'providerReturnEvent'>) => {
    lifecycleState.value = mapPaymentLifecycleState({
      ...input,
      providerReturnEvent: providerReturnEvent.value,
    })

    return lifecycleState.value
  }

  const reset = () => {
    providerReturnEvent.value = parseProviderReturnEvent(undefined)
    lifecycleState.value = createPaymentLifecycleState('idle', { reason: 'initial' })

    return lifecycleState.value
  }

  return {
    lifecycleState: readonly(lifecycleState),
    providerReturnEvent: readonly(providerReturnEvent),
    actions,
    transitionTo,
    parseProviderReturn,
    applyVerifiedPaymentState,
    reset,
  }
}

const normalizeProviderReturnQuery = (
  query: ProviderReturnQuery | null | undefined,
): Array<[key: string, value: string]> => {
  if (!query) {
    return []
  }

  if (typeof query === 'string') {
    return parseQueryString(query)
  }

  return Object.entries(query).flatMap(([key, value]) => normalizeQueryValues(key, value))
}

const parseQueryString = (query: string): Array<[key: string, value: string]> => {
  const withoutHash = query.split('#')[0] ?? ''
  const queryStart = withoutHash.indexOf('?')
  const rawQuery = queryStart >= 0 ? withoutHash.slice(queryStart + 1) : withoutHash

  if (!rawQuery || (queryStart < 0 && !rawQuery.includes('=') && !rawQuery.includes('&'))) {
    return []
  }

  return rawQuery
    .split('&')
    .filter(Boolean)
    .flatMap((part) => {
      const separatorIndex = part.indexOf('=')
      const rawKey = separatorIndex >= 0 ? part.slice(0, separatorIndex) : part
      const rawValue = separatorIndex >= 0 ? part.slice(separatorIndex + 1) : 'true'
      const key = safeDecodeURIComponent(rawKey)
      const value = safeDecodeURIComponent(rawValue)

      return key ? [[key, value]] : []
    })
}

const normalizeQueryValues = (
  key: string,
  value: ProviderReturnQueryValue | readonly ProviderReturnQueryValue[],
): Array<[key: string, value: string]> => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => normalizeQueryValues(key, item))
  }

  if (value === null || value === undefined) {
    return []
  }

  return [[key, String(value)]]
}

const categorizeProviderReturnHint = (
  key: string,
  value: string,
): Exclude<ProviderReturnEventType, 'none'> | undefined => {
  const normalizedKey = normalizeToken(key)
  const normalizedValue = normalizeToken(value)

  if (!RETURN_HINT_KEYS.has(key) && !RETURN_HINT_KEYS.has(normalizedKey)) {
    return undefined
  }

  if (normalizedKey === 'success' && isTruthyQueryValue(normalizedValue)) {
    return 'provider_returned_success'
  }

  if (['cancel', 'cancelled', 'canceled'].includes(normalizedKey) && isTruthyQueryValue(normalizedValue)) {
    return 'provider_returned_cancel'
  }

  if (['error', 'failure'].includes(normalizedKey) && normalizedValue !== 'false') {
    return 'provider_returned_failure'
  }

  if (SUCCESS_PROVIDER_HINTS.has(normalizedValue)) {
    return 'provider_returned_success'
  }

  if (FAILURE_PROVIDER_HINTS.has(normalizedValue)) {
    return 'provider_returned_failure'
  }

  if (CANCEL_PROVIDER_HINTS.has(normalizedValue)) {
    return 'provider_returned_cancel'
  }

  if (PENDING_PROVIDER_HINTS.has(normalizedValue)) {
    return 'provider_returned_pending'
  }

  return 'provider_returned_unknown'
}

const selectPaymentLifecycleTransaction = (
  input: MapPaymentLifecycleInput,
): PaymentLifecycleRawTransaction | PaymentLifecycleRawPayment | undefined => {
  if (input.transaction) {
    return input.transaction
  }

  if (input.payment?.transaction) {
    return input.payment.transaction
  }

  const transactions = [
    ...(input.transactions ?? []),
    ...(input.payment?.transactions ?? []),
    ...(input.order?.transactions ?? []),
  ].filter(Boolean)

  if (transactions.length) {
    return sortTransactionsByAttemptDate(transactions)[transactions.length - 1]
  }

  if (input.order?.transaction) {
    return input.order.transaction
  }

  if (input.payment && normalizeTransactionState(input.payment)) {
    return input.payment
  }

  return undefined
}

const sortTransactionsByAttemptDate = (
  transactions: readonly PaymentLifecycleRawTransaction[],
): PaymentLifecycleRawTransaction[] => {
  return [...transactions].sort(
    (left, right) => getTransactionAttemptTimestamp(left) - getTransactionAttemptTimestamp(right),
  )
}

const getTransactionAttemptTimestamp = (transaction: PaymentLifecycleRawTransaction): number => {
  const createdAt = parseTimestamp(transaction.createdAt)
  const updatedAt = parseTimestamp(transaction.updatedAt)

  return createdAt || updatedAt
}

const parseTimestamp = (value: unknown): number => {
  if (typeof value !== 'string') {
    return 0
  }

  const timestamp = Date.parse(value)

  return Number.isFinite(timestamp) ? timestamp : 0
}

const normalizeTransactionState = (
  source: PaymentLifecycleRawTransaction | PaymentLifecycleRawPayment | null | undefined,
): string | undefined => {
  if (!source) {
    return undefined
  }

  return firstString(
    source.stateMachineState?.technicalName,
    source.stateMachineState?.translated?.technicalName,
    source.technicalName,
    source.transactionState,
    source.paymentState,
    source.status,
    source.state,
    source.stateMachineState?.name,
    source.stateMachineState?.translated?.name,
  )
}

const getPaymentLifecyclePaymentMethod = (
  source: PaymentLifecycleRawTransaction | PaymentLifecycleRawPayment,
): PaymentLifecycleRawPaymentMethod | null | undefined => {
  return 'paymentMethod' in source ? source.paymentMethod : undefined
}

const firstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }

  return undefined
}

const buildPaymentLifecycleAction = (
  definition: PaymentLifecycleActionDefinition,
  context: PaymentLifecycleActionContext,
): PaymentLifecycleActionMeta => {
  const href = getPaymentLifecycleActionHref(definition, context)
  const reason = getPaymentLifecycleActionDisabledReason(definition, context, href)

  return {
    action: definition.action,
    enabled: !reason,
    primary: definition.primary,
    states: definition.states,
    href,
    reason,
  }
}

const getPaymentLifecycleActionDisabledReason = (
  definition: PaymentLifecycleActionDefinition,
  context: PaymentLifecycleActionContext,
  href?: string,
): string | undefined => {
  if (definition.action === 'retry_payment' && context.canRetryPayment === false) {
    return 'retry_payment_disabled'
  }

  if (definition.action === 'change_payment' && context.canChangePayment === false) {
    return 'change_payment_disabled'
  }

  if (definition.action === 'cancel_order' && context.canCancelOrder === false) {
    return 'cancel_order_disabled'
  }

  if (definition.requiresHref && !href) {
    return `missing_${definition.hrefContext}`
  }

  return undefined
}

const getPaymentLifecycleActionHref = (
  definition: PaymentLifecycleActionDefinition,
  context: PaymentLifecycleActionContext,
): string | undefined => {
  if (!definition.hrefContext) {
    return undefined
  }

  return context[definition.hrefContext] ?? undefined
}

const normalizeToken = (value: string): string => {
  return value.trim().replace(/\s+/g, '_').toLowerCase()
}

export const INVOICE_PAYMENT_TECHNICAL_NAME = 'payment_invoicepayment'

const WALLEE_PAYMENT_METHOD_TOKEN = 'wallee'

const isInvoicePaymentMethod = (paymentMethod: PaymentLifecycleRawPaymentMethod): boolean => {
  const technicalName = firstString(paymentMethod.technicalName, paymentMethod.translated?.technicalName)

  return technicalName ? normalizeToken(technicalName) === INVOICE_PAYMENT_TECHNICAL_NAME : false
}

// "Soon" products ("noch nicht erschienen") ship at a later date, so they may only be
// paid by invoice; an all-available cart may use every configured method (CARW-1699).
export const selectableCheckoutPaymentMethods = <T extends PaymentLifecycleRawPaymentMethod>(
  paymentMethods: T[],
  cartHasSoonProducts: boolean,
): T[] => {
  if (cartHasSoonProducts) {
    return paymentMethods.filter((paymentMethod) => isInvoicePaymentMethod(paymentMethod))
  }

  return paymentMethods
}

const isWalleePaymentMethod = (paymentMethod: PaymentLifecycleRawPaymentMethod): boolean => {
  const identifiers = [
    firstString(paymentMethod.technicalName, paymentMethod.translated?.technicalName),
    firstString(paymentMethod.handlerIdentifier),
  ]

  return identifiers.some(
    (identifier) => identifier && normalizeToken(identifier).includes(WALLEE_PAYMENT_METHOD_TOKEN),
  )
}

export const requiresExternalPaymentProvider = (
  paymentMethod: PaymentLifecycleRawPaymentMethod | null | undefined,
): boolean => {
  if (!paymentMethod || paymentMethod.synchronous === true || isInvoicePaymentMethod(paymentMethod)) {
    return false
  }

  return isWalleePaymentMethod(paymentMethod) || paymentMethod.afterOrderEnabled === true
}

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '))
  } catch {
    return value
  }
}

const isTruthyQueryValue = (value: string): boolean => {
  return ['1', 'true', 'yes', 'y', 'success', 'successful', 'paid'].includes(value)
}

const isNonAsyncAcceptedPaymentMethod = (
  paymentMethod: PaymentLifecycleRawPaymentMethod | null | undefined,
): boolean => {
  if (!paymentMethod) {
    return false
  }

  if (paymentMethod.asynchronous === false || paymentMethod.synchronous === true) {
    return true
  }

  return isInvoicePaymentMethod(paymentMethod)
}
